import { Box, Grid } from '@mui/material';

import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  TextField,
  ProductGroupSelectField,
  Select,
} from '@occc-app/design-system';
import { Typography } from '@occc/ui-design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { LocationMultipleSelectField } from '@occc-app/routes/Location/components/LocationSelect/LocationMultipleSelectField';
import { rollingWindowOptions } from '@occc-app/routes/Audience/components/constants';
import { PurchaseFormValues } from '@occc-app/routes/Audience/components/Purchases/types';

import { useField } from 'react-final-form';
import { LocationGroupMultipleSelect } from '@occc/feat-constraints';
import { countProductsBy, hasPurchased, timePeriodOptions } from './constants';

import { TransactionTimePeriod } from '../../types';

export type PurchasesFormArrayItemProps = {
  fieldArrayName: string;
};

export const PurchasesFormArrayItem = ({ fieldArrayName }: PurchasesFormArrayItemProps) => {
  const {
    input: { value: values },
  } = useField<PurchaseFormValues>(fieldArrayName, { subscription: { value: true } });

  return (
    <>
      <Grid container columnSpacing={3}>
        <Grid item xs={8}>
          <ProductGroupSelectField
            inputProps={{
              margin: 'none',
            }}
            name={`${fieldArrayName}.productGroups`}
            label='Eligible Product Groups (Product in Any Group)'
          />
        </Grid>
        <Grid item xs={4}>
          <Select name={`${fieldArrayName}.countProductsBy`} data={countProductsBy} />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Typography paragraph variant='subtitle1'>
          Member has Purchased
        </Typography>
        <Grid item container spacing={3} xs={12}>
          <Grid item xs={12} md={4}>
            <Select name={`${fieldArrayName}.type`} data={hasPurchased} />
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <TextField
              name={`${fieldArrayName}.threshold`}
              label='Threshold'
              fieldProps={{
                parse: parseNumber,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box mt={3}>
        <Typography paragraph variant='subtitle1'>
          Period of the purchase
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3}>
            <Select name={`${fieldArrayName}.timePeriodType`} data={timePeriodOptions} />
          </Grid>

          {values.timePeriodType === TransactionTimePeriod.RollingWindow && (
            <Grid item xs={12} sm={4} md={3}>
              <Select
                name={`${fieldArrayName}.timeWindow`}
                label='Rolling Window'
                data={rollingWindowOptions}
                formControlProps={{ size: 'medium', margin: 'none' }}
              />
            </Grid>
          )}

          {values.timePeriodType === TransactionTimePeriod.FixedMonth && (
            <Grid item xs={12} sm={4} md={3}>
              <KeyboardDatePicker
                label='From'
                name={`${fieldArrayName}.fixedMonth`}
                views={['year', 'month']}
                format='yyyy/MM'
              />
            </Grid>
          )}
          {values.timePeriodType === TransactionTimePeriod.FixedWindow && (
            <>
              <Grid item xs={12} sm={4} md={3}>
                <KeyboardDateTimePicker
                  label='Start Date'
                  name={`${fieldArrayName}.fixedWindow.startDate`}
                  maxDate={values?.fixedWindow?.endDate || undefined}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <KeyboardDateTimePicker
                  label='End Date'
                  name={`${fieldArrayName}.fixedWindow.endDate`}
                  minDate={values?.fixedWindow?.startDate || undefined}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      <Box mt={3}>
        <Typography paragraph variant='subtitle1'>
          Locations
        </Typography>

        <LocationMultipleSelectField
          inputProps={{
            margin: 'none',
          }}
          name={`${fieldArrayName}.locations`}
          label='Locations'
        />
      </Box>

      <Box mt={3}>
        <Typography paragraph variant='subtitle1'>
          Location Groups
        </Typography>

        <LocationGroupMultipleSelect
          inputProps={{
            margin: 'none',
          }}
          name={`${fieldArrayName}.locationGroups`}
          label='Location Groups'
        />
      </Box>
    </>
  );
};
